/**
 * Mock design provider — the OFFLINE stand-in for OpenAI. It returns valid,
 * schema-conformant Design JSON so the entire pipeline (compile → assess →
 * editor) runs with no key and so tests are deterministic. Concepts are
 * built on DIFFERENT layout archetypes (not color variations), which
 * exercises the compiler and demonstrates the diversity contract. Real
 * premium diversity comes from the live model; this is the safety net.
 */

import { mostReadableOn } from '../designSpec/colorUtils';

const PALETTES = [
  { primary: '#7c3aed', secondary: '#4c1d95', accent: '#f59e0b', background: '#ffffff', text: '#111827' },
  { primary: '#0ea5e9', secondary: '#0369a1', accent: '#f43f5e', background: '#0f172a', text: '#f8fafc' },
  { primary: '#16a34a', secondary: '#166534', accent: '#f59e0b', background: '#f0fdf4', text: '#14532d' },
  { primary: '#ea580c', secondary: '#9a3412', accent: '#facc15', background: '#fff7ed', text: '#7c2d12' },
  { primary: '#db2777', secondary: '#9d174d', accent: '#22d3ee', background: '#fdf2f8', text: '#831843' },
];

const HEAD_FONTS = ['Poppins', 'Montserrat', 'Playfair Display', 'Oswald', 'Nunito'];
const ARCHETYPES = ['hero-top', 'split-left', 'centered-poster', 'color-band', 'feature-grid'];

const text = (id, x, y, w, h, t, size, weight, color, align = 'left') =>
  ({ id, type: 'text', x, y, width: w, height: h, text: t, fontSize: size, fontWeight: weight, color, align });

const button = (id, x, y, w, h, label, fill, color) =>
  ({ id, type: 'button', x, y, width: w, height: h, text: label, fill, color, align: 'center' });

const image = (id, x, y, w, h, query) => ({ id, type: 'image', x, y, width: w, height: h, query, fit: 'cover' });

const shape = (id, x, y, w, h, kind, fill, opacity = 1) =>
  ({ id, type: 'shape', x, y, width: w, height: h, shape: kind, fill, opacity });

/** Build one concept on a chosen archetype. Objects are laid out in
 *  non-overlapping regions so the assessment gate passes offline. */
const buildConcept = (brief, index) => {
  const { width: W, height: H } = brief.canvas;
  const pal = PALETTES[index % PALETTES.length];
  const headFont = HEAD_FONTS[index % HEAD_FONTS.length];
  const archetype = ARCHETYPES[index % ARCHETYPES.length];
  const onBg = mostReadableOn(pal.background, [pal.text, '#111111', '#ffffff']);
  const onAccent = mostReadableOn(pal.accent, ['#111111', '#ffffff']);
  const onPrimary = mostReadableOn(pal.primary, ['#111111', '#ffffff']);
  const subject = (brief.category || brief.industry || brief.documentType || 'business').toLowerCase();
  const m = Math.round(W * 0.08);
  const objects = [];

  if (archetype === 'hero-top') {
    objects.push(image('hero', 0, 0, W, Math.round(H * 0.42), subject));
    objects.push(text('h', m, Math.round(H * 0.46), W - m * 2, Math.round(H * 0.12), (brief.purpose || 'Big Announcement').toUpperCase(), Math.round(W / 12), 800, onBg, 'left'));
    objects.push(text('s', m, Math.round(H * 0.60), W - m * 2, Math.round(H * 0.08), brief.audience ? `For ${brief.audience}` : 'Something special awaits', Math.round(W / 28), 400, onBg, 'left'));
    objects.push(button('cta', m, Math.round(H * 0.72), Math.round(W * 0.42), Math.round(H * 0.08), 'Learn More', pal.primary, onPrimary));
    objects.push(text('f', m, Math.round(H * 0.9), W - m * 2, Math.round(H * 0.05), 'yourbrand.com', Math.round(W / 34), 400, onBg, 'left'));
  } else if (archetype === 'split-left') {
    objects.push(image('hero', 0, 0, Math.round(W * 0.5), H, subject));
    const rx = Math.round(W * 0.55);
    const rw = W - rx - m;
    objects.push(text('h', rx, Math.round(H * 0.18), rw, Math.round(H * 0.18), brief.purpose || 'Your Headline', Math.round(W / 14), 800, onBg, 'left'));
    objects.push(text('s', rx, Math.round(H * 0.40), rw, Math.round(H * 0.14), 'A crisp supporting line that sells the idea.', Math.round(W / 30), 400, onBg, 'left'));
    objects.push(button('cta', rx, Math.round(H * 0.60), Math.round(rw * 0.8), Math.round(H * 0.08), 'Shop Now', pal.accent, onAccent));
  } else if (archetype === 'centered-poster') {
    objects.push(text('h', m, Math.round(H * 0.22), W - m * 2, Math.round(H * 0.2), (brief.purpose || 'Grand Event').toUpperCase(), Math.round(W / 9), 900, onBg, 'center'));
    objects.push(text('s', m, Math.round(H * 0.46), W - m * 2, Math.round(H * 0.1), brief.brandPersonality ? `${brief.brandPersonality} experience` : 'You are invited', Math.round(W / 24), 400, onBg, 'center'));
    objects.push(button('cta', Math.round(W * 0.3), Math.round(H * 0.64), Math.round(W * 0.4), Math.round(H * 0.08), 'RSVP', pal.primary, onPrimary));
    objects.push(text('f', m, Math.round(H * 0.86), W - m * 2, Math.round(H * 0.06), 'date · venue · contact', Math.round(W / 30), 400, onBg, 'center'));
  } else if (archetype === 'color-band') {
    objects.push(shape('band', 0, 0, W, Math.round(H * 0.34), 'rect', pal.primary));
    objects.push(text('h', m, Math.round(H * 0.1), W - m * 2, Math.round(H * 0.16), brief.purpose || 'Special Offer', Math.round(W / 12), 800, onPrimary, 'left'));
    objects.push(image('hero', m, Math.round(H * 0.4), W - m * 2, Math.round(H * 0.32), subject));
    objects.push(text('s', m, Math.round(H * 0.76), W - m * 2, Math.round(H * 0.08), 'Supporting detail goes here.', Math.round(W / 28), 400, onBg, 'left'));
    objects.push(button('cta', m, Math.round(H * 0.87), Math.round(W * 0.44), Math.round(H * 0.07), 'Get Started', pal.accent, onAccent));
  } else {
    // feature-grid
    objects.push(text('h', m, Math.round(H * 0.08), W - m * 2, Math.round(H * 0.1), brief.purpose || 'Why Choose Us', Math.round(W / 14), 800, onBg, 'center'));
    const cw = Math.round((W - m * 3) / 2);
    const ch = Math.round(H * 0.24);
    [[m, 0.24], [m * 2 + cw, 0.24], [m, 0.52], [m * 2 + cw, 0.52]].forEach((pos, i) => {
      objects.push(shape(`card${i}`, pos[0], Math.round(H * pos[1]), cw, ch, 'roundedRect', i % 2 ? pal.secondary : pal.primary, 0.12));
      objects.push(text(`ct${i}`, pos[0] + 16, Math.round(H * pos[1]) + 16, cw - 32, Math.round(ch * 0.6), `Feature ${i + 1}`, Math.round(W / 30), 700, onBg, 'left'));
    });
    objects.push(button('cta', Math.round(W * 0.3), Math.round(H * 0.82), Math.round(W * 0.4), Math.round(H * 0.08), 'Contact Us', pal.accent, onAccent));
  }

  return {
    concept: `${archetype} · ${brief.documentType || 'template'}`,
    canvas: { width: W, height: H, background: pal.background },
    theme: { name: archetype, mood: brief.colorMood || 'balanced' },
    typography: { heading: { family: headFont, weight: 800 }, body: { family: 'Inter', weight: 400 } },
    colors: pal,
    sections: [],
    objects,
  };
};

/**
 * Offline generator: N distinct concepts. Async to match the real provider.
 */
export const mockGenerateDesigns = async (brief, count = 1) => {
  const n = Math.max(1, Math.min(10, Math.round(count) || 1));
  return Array.from({ length: n }, (_, i) => buildConcept(brief, i));
};

export { ARCHETYPES };
